// @ts-nocheck
// Ported byte-identically from v1
// components/partials/dashboard/agent/PeriodCallsScatterModal.js.
// Scatter chart of (time-of-call, duration) per outcome bucket. Opened by
// clicking a period donut inside AgentCallStatsPanel.
import { useMemo } from 'react';
import Chart from 'react-apexcharts';
import moment from 'moment';
import { callDurationSeconds, callOutcome } from '../ctiHelpers';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const OUTCOME_COLORS = {
  answered: '#16a34a',
  failed: '#dc2626',
  abandoned: '#d97706',
};

function buildScatterSeries(calls = []) {
  const buckets: any = {
    answered: [],
    failed: [],
    abandoned: [],
  };

  (calls || []).forEach((call) => {
    const t = call.time_of_call || call.start_time || call.created_at;
    if (!t) return;
    const x = new Date(t).getTime();
    const y = callDurationSeconds(call);
    buckets[callOutcome(call)].push({ x, y });
  });

  return [
    { name: 'Answered', data: buckets.answered, color: OUTCOME_COLORS.answered },
    { name: 'Failed', data: buckets.failed, color: OUTCOME_COLORS.failed },
    { name: 'Abandoned', data: buckets.abandoned, color: OUTCOME_COLORS.abandoned },
  ].filter((s) => s.data.length > 0);
}

export default function PeriodCallsScatterModal({
  show,
  onHide,
  periodLabel = '',
  calls = [],
  loading = false,
  theme = 'light',
}) {
  const isDark = theme === 'dark';
  const series = useMemo(() => buildScatterSeries(calls), [calls]);
  const totalPoints = series.reduce((sum, s) => sum + s.data.length, 0);

  const options = useMemo(
    () => ({
      chart: {
        type: 'scatter',
        background: 'transparent',
        toolbar: { show: true, tools: { download: true, zoom: true, reset: true } },
        zoom: { enabled: true, type: 'xy' },
        fontFamily: 'inherit',
      },
      colors: series.map((s) => s.color),
      legend: {
        position: 'top',
        labels: { colors: isDark ? '#94a3b8' : '#64748b' },
      },
      markers: {
        size: 7,
        strokeWidth: 0,
        hover: { size: 9 },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: isDark ? 'rgba(148,163,184,0.15)' : 'rgba(148,163,184,0.25)',
        strokeDashArray: 4,
      },
      xaxis: {
        type: 'datetime',
        labels: {
          style: { colors: isDark ? '#94a3b8' : '#64748b', fontSize: '11px' },
          datetimeFormatter: {
            year: 'yyyy',
            month: 'dd MMM',
            day: 'dd MMM',
            hour: 'HH:mm',
          },
        },
        title: {
          text: 'Call time',
          style: { color: isDark ? '#cbd5e1' : '#475569', fontSize: '12px' },
        },
      },
      yaxis: {
        labels: {
          style: { colors: isDark ? '#94a3b8' : '#64748b', fontSize: '11px' },
        },
        title: {
          text: 'Duration (seconds)',
          style: { color: isDark ? '#cbd5e1' : '#475569', fontSize: '12px' },
        },
        min: 0,
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        custom({ seriesIndex, dataPointIndex, w }) {
          const point = w.config.series[seriesIndex].data[dataPointIndex];
          if (!point) return '';
          const label = w.config.series[seriesIndex].name;
          return `<div class="px-2 py-1"><strong>${label}</strong><br/>${moment(point.x).format('DD MMM YYYY, HH:mm:ss')}<br/>Duration: ${point.y}s</div>`;
        },
      },
    }),
    [isDark, series]
  );

  const chartSeries = series.map(({ name, data }) => ({ name, data }));

  return (
    <Dialog open={!!show} onOpenChange={(next) => !next && onHide?.()}>
      <DialogContent
        className="agent-period-scatter-modal z-[1150] max-w-3xl"
        overlayClassName="z-[1150]"
        data-theme={theme}
      >
        <DialogHeader className="agent-period-scatter-modal__header">
          <DialogTitle>{periodLabel} — Call scatter</DialogTitle>
        </DialogHeader>
        <div className="agent-period-scatter-modal__body">
          {loading ? (
            <div className="agent-call-stats-empty-chart">Loading calls…</div>
          ) : totalPoints > 0 ? (
            <Chart options={options as any} series={chartSeries} type="scatter" height={360} />
          ) : (
            <div className="agent-call-stats-empty-chart">No calls in this period</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
